import { useState } from 'react';
import { Button, cn, Stack } from '@prenuvo/halo-foundation';

import { usePatientStudiesQuery } from '@/api/queries';
import { FCT } from '@/i18n';
import { useStudyGuard } from '@/shared/guard';

import { SidePanel } from '../../../../component/side-panel';
import { PdfViewer } from './pdf-viewer/pdf-viewer.component';
import { PatientDocIframe } from './patient-doc-iframe.component';
import { PatientDocs } from './patient-docs.component';
import { PatientDoc } from './patient-docs.model';
import { BUTTON_CLASS_OVERRIDES } from './patient-docs-panel.constant';
import { generateMifLink } from './pdf-docs.util';

interface PatientDocsSidePanelProps {
  isOpen: boolean;
  onOpenChange: (value: boolean) => void;
}

export function PatientDocsSidePanel({ isOpen, onOpenChange }: PatientDocsSidePanelProps) {
  const { studyId } = useStudyGuard();

  const { previousStudyIds } = usePatientStudiesQuery();

  const mifLink = generateMifLink(studyId);

  const [pdfLink, setActivePdfLink] = useState<PatientDoc>({ type: 'html', url: mifLink });

  return (
    <div>
      <Button
        className={{
          root: cn({
            [BUTTON_CLASS_OVERRIDES]: isOpen,
          }),
        }}
        data-testid="patient-documents-button"
        onClick={() => onOpenChange(!isOpen)}
        size="sm"
        variant="text"
      >
        <Stack alignment="center">
          {FCT.PATIENT_DOCS.SIDE_PANEL.TRIGGER}

          {previousStudyIds.length > 0 && (
            <Stack
              alignment="center"
              className="mt-0.5 h-6 shrink-0 rounded-lg px-3 dark:bg-blue-500"
              justification="center"
            >
              <span className="mb-0.5 dark:text-white">{FCT.PATIENT_DOCS.SIDE_PANEL.PRIORS}</span>
            </Stack>
          )}
        </Stack>
      </Button>

      <SidePanel
        isOpen={isOpen}
        onClose={() => onOpenChange(false)}
        title={
          <PatientDocs
            mifLink={mifLink}
            onPatientLinkChange={(value) => setActivePdfLink(value)}
            pdfLink={pdfLink ?? mifLink}
            previousStudyIds={previousStudyIds}
          />
        }
      >
        {isOpen && (
          <div className="overflow-auto px-2 pb-2">
            {pdfLink.type === 'html' && <PatientDocIframe url={pdfLink.url} />}
            {pdfLink.type === 'pdf' && <PdfViewer url={pdfLink.url} />}
          </div>
        )}
      </SidePanel>
    </div>
  );
}
